/**
 * PSE-2 — Federal dockets sentiment fetcher.
 *
 * Three sub-fetchers fan into a single `'federal-dockets'` source:
 *  1. Regulations.gov — `https://api.regulations.gov/v4/comments`
 *     API key injected server-side via `/api/proxy` (REGULATIONS_GOV_API_KEY).
 *  2. FERC eLibrary — HTML scrape. Public, no auth.
 *  3. NRC ADAMS — reuse `searchAdams` from `nrcAdams.ts`. Public, no auth.
 *
 * NEVER throws. Auth-missing on Regulations.gov is a soft failure;
 * FERC + ADAMS still run independently.
 */
import type { PublicSentimentEntry } from '../../types/siteEvidence';
import type { PublicSentimentSourceResult } from '../publicSentimentOrchestrator';
import { fetchWithTimeoutCircuit } from './pseFetchCircuit';
import {
  RETRYABLE_HTTP_STATUS,
  parseRetryAfterMs,
  resolveRetryWaitMs,
} from '../../lib/httpRetry';
import {
  abortableDelay,
  raceWithAbort,
  rethrowIfAborted,
  throwIfAborted,
} from '../../lib/abortSignal';
import {
  classifyBtmTech,
  lengthBasedConfidence,
  polarityScore,
  truncateSnippet,
} from './sentimentPolarity';

/** Delay between distinct FERC queries to avoid burst rate limits. */
export const FERC_QUERY_DELAY_MS = 300;

const FERC_429_BACKOFF_MS = [30_000, 120_000, 300_000] as const;

/** Query-level caches — dedupe across BTM-tech orchestrator dispatches. */
const regGovCache = new Map<string, PublicSentimentEntry[]>();
const fercCache = new Map<string, PublicSentimentEntry[]>();
const adamsCache = new Map<string, PublicSentimentEntry[]>();

let fercCircuitOpen = false;
let regulationsGovDisabled = false;
let didWarnMissingRegulationsGovKey = false;
let didWarnFercRateLimited = false;

/** Reset session state at the start of each orchestrator run (or in tests). */
export function resetFederalDocketsFetchSession(): void {
  regGovCache.clear();
  fercCache.clear();
  adamsCache.clear();
  fercCircuitOpen = false;
  regulationsGovDisabled = false;
  didWarnMissingRegulationsGovKey = false;
  didWarnFercRateLimited = false;
}

function fercRetryWaitMs(attempt: number, retryAfterHeader: string | null | undefined): number {
  const fromHeader = parseRetryAfterMs(retryAfterHeader);
  if (fromHeader != null && fromHeader > 0) return fromHeader;
  return FERC_429_BACKOFF_MS[Math.min(attempt - 1, FERC_429_BACKOFF_MS.length - 1)] ?? 300_000;
}

/** Bounded retry for proxied federal-docket fetches (429 / 5xx). */
async function fetchProxiedWithRetry(
  url: string,
  timeoutMs: number,
  label: string,
  maxAttempts = 3,
  signal?: AbortSignal,
): Promise<Response> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    throwIfAborted(signal);
    const res = await fetchWithTimeoutCircuit(label, url, timeoutMs, signal);
    throwIfAborted(signal);
    if (res.ok || attempt >= maxAttempts || !RETRYABLE_HTTP_STATUS.has(res.status)) {
      return res;
    }
    const waitMs = label === 'ferc' && res.status === 429
      ? fercRetryWaitMs(attempt, res.headers.get('Retry-After'))
      : resolveRetryWaitMs(attempt, res.status, res.headers.get('Retry-After'));
    if (label !== 'ferc' || attempt === 1) {
      console.warn(`[pse:federal-dockets:${label}] HTTP ${res.status} attempt ${attempt}/${maxAttempts} — retrying in ${waitMs}ms`);
    }
    await abortableDelay(waitMs, signal);
  }
  throw new Error(`[pse:federal-dockets:${label}] retry loop exhausted`);
}

/* ── Regulations.gov fetcher ───────────────────────────────────────── */

const REGULATIONS_GOV_BASE = 'https://api.regulations.gov/v4/comments';

export const GEMINI_SENTIMENT_COST_CAP = 3;

interface RegulationsGovComment {
  id?: string;
  attributes?: {
    title?: string;
    comment?: string;
    submitterName?: string;
    postedDate?: string;
    objectId?: string;
    docketId?: string;
  };
  links?: {
    self?: string;
  };
}

interface RegulationsGovResponse {
  data?: RegulationsGovComment[];
  meta?: { totalElements?: number };
}

async function fetchRegulationsGovOne(
  query: string,
  signal?: AbortSignal,
): Promise<PublicSentimentEntry[]> {
  const params = new URLSearchParams({
    'filter[searchTerm]': query,
    'page[size]': '20',
  });
  const url = `${REGULATIONS_GOV_BASE}?${params.toString()}`;
  const res = await fetchProxiedWithRetry(url, 20_000, 'regulations-gov', 3, signal);
  if (res.status === 424) {
    regulationsGovDisabled = true;
    throw new Error('regulations-gov: server API key not configured');
  }
  if (!res.ok) throw new Error(`regulations-gov: HTTP ${res.status}`);
  const text = await res.text();
  if (!text.trim()) return [];
  let parsed: RegulationsGovResponse;
  try {
    parsed = JSON.parse(text) as RegulationsGovResponse;
  } catch {
    throw new Error('regulations-gov: malformed response');
  }
  const comments = parsed.data ?? [];
  const out: PublicSentimentEntry[] = [];
  for (const c of comments) {
    const id = c.id?.trim();
    const title = c.attributes?.title?.trim() ?? '';
    const body = c.attributes?.comment?.trim() ?? '';
    if (!id) continue;
    const entryText = `${title} ${body}`.trim();
    if (entryText.length === 0) continue;
    out.push({
      sourceId: 'federal-dockets',
      fetchedAt: new Date().toISOString(),
      btmTech: classifyBtmTech(entryText),
      sentimentScore: polarityScore(entryText),
      confidence: lengthBasedConfidence(entryText),
      ...(c.attributes?.postedDate && Number.isFinite(Date.parse(c.attributes.postedDate)) ? { observedDate: new Date(c.attributes.postedDate).toISOString(), dateBasis: 'published' as const } : {}),
      supportingSnippet: truncateSnippet(entryText),
      url: `https://www.regulations.gov/comment/${id}`,
    });
  }
  return out;
}

/* ── FERC eLibrary fetcher ─────────────────────────────────────────── */

const FERC_ELIBRARY_BASE = 'https://elibrary.ferc.gov/eLibrary/search';

export function parseFercHtml(html: string): Array<{ title: string; url: string; excerpt: string }> {
  if (typeof html !== 'string' || html.length === 0) return [];
  const out: Array<{ title: string; url: string; excerpt: string }> = [];
  const tableMatch = html.match(/<table[^>]*class="[^"]*srchResults[^"]*"[^>]*>([\s\S]*?)<\/table>/);
  if (!tableMatch) return [];
  const rowPattern = /<tr[^>]*>([\s\S]*?)<\/tr>/g;
  let match: RegExpExecArray | null;
  while ((match = rowPattern.exec(tableMatch[1])) !== null) {
    const row = match[1];
    const titleMatch = row.match(/<a[^>]+href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/);
    if (!titleMatch) continue;
    const url = titleMatch[1].trim();
    const title = titleMatch[2].replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    const excerptMatch = row.match(/<td[^>]*class="[^"]*excerpt[^"]*"[^>]*>([\s\S]*?)<\/td>/);
    const excerpt = excerptMatch
      ? excerptMatch[1].replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim()
      : '';
    if (url && title) out.push({ title, url, excerpt });
  }
  return out;
}

async function fetchFercOne(
  query: string,
  signal?: AbortSignal,
): Promise<PublicSentimentEntry[]> {
  const params = new URLSearchParams({ searchKeywords: query });
  const url = `${FERC_ELIBRARY_BASE}?${params.toString()}`;
  const res = await fetchProxiedWithRetry(url, 20_000, 'ferc', 3, signal);
  if (!res.ok) throw new Error(`ferc: HTTP ${res.status}`);
  const html = await res.text();
  const blocks = parseFercHtml(html);
  return blocks.map(b => {
    const text = `${b.title} ${b.excerpt}`.trim();
    return {
      sourceId: 'federal-dockets',
      fetchedAt: new Date().toISOString(),
      btmTech: classifyBtmTech(text),
      sentimentScore: polarityScore(text),
      confidence: lengthBasedConfidence(text),
      supportingSnippet: truncateSnippet(b.excerpt || b.title),
      url: b.url.startsWith('http') ? b.url : `https://elibrary.ferc.gov${b.url}`,
    } satisfies PublicSentimentEntry;
  });
}

/* ── NRC ADAMS wrapper ─────────────────────────────────────────────── */

async function fetchAdamsOne(
  query: string,
  state: string | null,
  signal?: AbortSignal,
): Promise<PublicSentimentEntry[]> {
  const { searchAdams } = await import('./nrcAdams');
  const { data } = await searchAdams(state, query, 20, signal);
  const out: PublicSentimentEntry[] = [];
  for (const d of data.documents) {
    const title = d.title?.trim() ?? '';
    if (!title || !d.url) continue;
    const text = `${title} ${d.documentType ?? ''}`.trim();
    out.push({
      sourceId: 'federal-dockets',
      fetchedAt: new Date().toISOString(),
      btmTech: classifyBtmTech(text),
      sentimentScore: polarityScore(text),
      confidence: lengthBasedConfidence(text),
      ...(d.date && Number.isFinite(Date.parse(d.date)) ? { observedDate: new Date(d.date).toISOString(), dateBasis: 'published' as const } : {}),
      supportingSnippet: truncateSnippet(title),
      url: d.url,
    });
  }
  return out;
}

/* ── Public entry point ────────────────────────────────────────────── */

export interface FederalDocketsFilters {
  /** Attempt-owned report cancellation/watchdog signal. */
  signal?: AbortSignal;
  state?: string;
  sentimentClassifier?: (text: string) => Promise<number>;
}

export async function fetchFederalDocketsSentiment(
  queries: string[],
  filters: FederalDocketsFilters = {},
): Promise<PublicSentimentSourceResult> {
  throwIfAborted(filters.signal);
  if (queries.length === 0) {
    return { status: 'unavailable', reason: 'no queries supplied', entries: [] };
  }

  const allEntries: PublicSentimentEntry[] = [];
  const errors: string[] = [];
  let attemptCount = 0;

  for (let qi = 0; qi < queries.length; qi++) {
    throwIfAborted(filters.signal);
    const q = queries[qi]!;

    if (!regulationsGovDisabled) {
      attemptCount++;
      const cacheKey = `reg:${q}`;
      try {
        let entries = regGovCache.get(cacheKey);
        if (!entries) {
          entries = await fetchRegulationsGovOne(q, filters.signal);
          throwIfAborted(filters.signal);
          regGovCache.set(cacheKey, entries);
        }
        allEntries.push(...entries);
      } catch (e) {
        rethrowIfAborted(filters.signal, e);
        const msg = (e as Error)?.message ?? String(e);
        if (msg.includes('server API key not configured') || msg.includes('HTTP 424')) {
          regulationsGovDisabled = true;
          if (!didWarnMissingRegulationsGovKey) {
            console.warn('[pse:federal-dockets:regulations-gov] REGULATIONS_GOV_API_KEY not configured on server — Regulations.gov skipped');
            didWarnMissingRegulationsGovKey = true;
          }
          errors.push('regulations-gov: server API key not configured');
        } else {
          errors.push(`regulations-gov "${q}": ${msg}`);
          console.warn(`[pse:federal-dockets:regulations-gov] ${msg}`);
        }
      }
    }

    if (!fercCircuitOpen) {
      if (qi > 0) await abortableDelay(FERC_QUERY_DELAY_MS, filters.signal);
      attemptCount++;
      const cacheKey = `ferc:${q}`;
      try {
        let entries = fercCache.get(cacheKey);
        if (!entries) {
          entries = await fetchFercOne(q, filters.signal);
          throwIfAborted(filters.signal);
          fercCache.set(cacheKey, entries);
        }
        allEntries.push(...entries);
      } catch (e) {
        rethrowIfAborted(filters.signal, e);
        const msg = (e as Error)?.message ?? String(e);
        errors.push(`ferc "${q}": ${msg}`);
        if (/HTTP 429/i.test(msg)) {
          fercCircuitOpen = true;
          if (!didWarnFercRateLimited) {
            console.warn('[pse:federal-dockets:ferc] FERC rate limited — skipping remaining queries this session');
            didWarnFercRateLimited = true;
          }
        } else {
          console.warn(`[pse:federal-dockets:ferc] ${msg}`);
        }
      }
    }

    attemptCount++;
    const adamsKey = `adams:${q}:${filters.state ?? ''}`;
    try {
      let entries = adamsCache.get(adamsKey);
      if (!entries) {
        entries = await fetchAdamsOne(q, filters.state ?? null, filters.signal);
        throwIfAborted(filters.signal);
        adamsCache.set(adamsKey, entries);
      }
      allEntries.push(...entries);
    } catch (e) {
      rethrowIfAborted(filters.signal, e);
      const msg = (e as Error)?.message ?? String(e);
      errors.push(`nrc-adams "${q}": ${msg}`);
      console.warn(`[pse:federal-dockets:nrc-adams] ${msg}`);
    }
  }

  if (filters.sentimentClassifier && allEntries.length > 0) {
    const targets = allEntries.slice(0, GEMINI_SENTIMENT_COST_CAP);
    for (let i = 0; i < targets.length; i++) {
      try {
        const next = await raceWithAbort(
          filters.sentimentClassifier(targets[i].supportingSnippet),
          filters.signal,
        );
        throwIfAborted(filters.signal);
        if (Number.isFinite(next)) {
          targets[i] = {
            ...targets[i],
            sentimentScore: Math.max(-1, Math.min(1, next)),
          };
          allEntries[i] = targets[i];
        }
      } catch (e) {
        rethrowIfAborted(filters.signal, e);
        console.warn(`[pse:federal-dockets:gemini-classifier] ${(e as Error)?.message ?? String(e)}`);
      }
    }
  }

  if (attemptCount === 0) {
    return { status: 'ok', entries: [] };
  }

  if (allEntries.length === 0 && errors.length >= attemptCount) {
    return { status: 'unavailable', reason: errors.join('; '), entries: [] };
  }

  const seen = new Set<string>();
  const deduped: PublicSentimentEntry[] = [];
  for (const e of allEntries) {
    if (seen.has(e.url)) continue;
    seen.add(e.url);
    deduped.push(e);
  }

  return { status: 'ok', entries: deduped };
}
